import type { ServerSnapshotMessage } from "@xiuxian/game-protocol";

export interface AttackFormationStyle {
  backRingColor: number;
  backRingAlpha: number;
  frontArcColor: number;
  frontArcAlpha: number;
  boundaryColor: number;
  boundaryAlpha: number;
  runeColor: number;
  runeAlpha: number;
  innerColor: number;
  innerAlpha: number;
}

interface RealmFormationPalette {
  backRingColor: number;
  frontArcColor: number;
  boundaryColor: number;
  runeColor: number;
  innerColor: number;
}

const REALM_FORMATION_PALETTES: readonly RealmFormationPalette[] = [
  {
    backRingColor: 0x2b8fb1,
    frontArcColor: 0x67e8f9,
    boundaryColor: 0x22d3ee,
    runeColor: 0xa5f3fc,
    innerColor: 0xecfeff,
  },
  {
    backRingColor: 0x1f8a4c,
    frontArcColor: 0x4ade80,
    boundaryColor: 0x86efac,
    runeColor: 0x22c55e,
    innerColor: 0xdcfce7,
  },
  {
    backRingColor: 0xb7791f,
    frontArcColor: 0xfacc15,
    boundaryColor: 0xfde047,
    runeColor: 0xf59e0b,
    innerColor: 0xfef3c7,
  },
  {
    backRingColor: 0x6d5bd0,
    frontArcColor: 0xa78bfa,
    boundaryColor: 0xc4b5fd,
    runeColor: 0x818cf8,
    innerColor: 0xede9fe,
  },
  {
    backRingColor: 0xc2410c,
    frontArcColor: 0xfb923c,
    boundaryColor: 0xfef3c7,
    runeColor: 0xf97316,
    innerColor: 0xffedd5,
  },
];

export function getRealmFormationPalette(level: number): RealmFormationPalette {
  const normalized = Number.isFinite(level) ? Math.floor(level) : 1;
  const realmIndex = Math.max(
    0,
    Math.min(4, Math.floor((normalized - 1) / 10)),
  );
  return REALM_FORMATION_PALETTES[realmIndex]!;
}

export function getAttackFormationStyle(
  phase: ServerSnapshotMessage["self"]["attackPhase"],
  isSelf: boolean,
  level: number,
): AttackFormationStyle {
  const palette = getRealmFormationPalette(level);
  const selfAlphaBonus = isSelf ? 1 : 0.82;
  if (phase === "windup") {
    return {
      backRingColor: palette.backRingColor,
      backRingAlpha: isSelf ? 0.24 : 0.18,
      frontArcColor: blendRgb(palette.frontArcColor, 0xfff1a8, 0.52),
      frontArcAlpha: isSelf ? 0.95 : 0.72,
      boundaryColor: blendRgb(palette.boundaryColor, 0xfff1a8, 0.42),
      boundaryAlpha: isSelf ? 0.9 : 0.66,
      runeColor: blendRgb(palette.runeColor, 0xfff1a8, 0.38),
      runeAlpha: isSelf ? 0.92 : 0.68,
      innerColor: blendRgb(palette.innerColor, 0xfff1a8, 0.34),
      innerAlpha: isSelf ? 0.42 : 0.3,
    };
  }
  if (phase === "recovery") {
    return {
      backRingColor: blendRgb(palette.backRingColor, 0x4f6278, 0.46),
      backRingAlpha: isSelf ? 0.12 : 0.08,
      frontArcColor: blendRgb(palette.frontArcColor, 0x98a2b8, 0.58),
      frontArcAlpha: isSelf ? 0.48 : 0.34,
      boundaryColor: blendRgb(palette.boundaryColor, 0xaeb8ca, 0.52),
      boundaryAlpha: isSelf ? 0.44 : 0.3,
      runeColor: blendRgb(palette.runeColor, 0xb6c0d4, 0.52),
      runeAlpha: isSelf ? 0.34 : 0.24,
      innerColor: blendRgb(palette.innerColor, 0xb6c0d4, 0.52),
      innerAlpha: isSelf ? 0.18 : 0.12,
    };
  }
  return {
    backRingColor: palette.backRingColor,
    backRingAlpha: 0.21 * selfAlphaBonus,
    frontArcColor: palette.frontArcColor,
    frontArcAlpha: isSelf ? 0.72 : 0.45,
    boundaryColor: palette.boundaryColor,
    boundaryAlpha: isSelf ? 0.72 : 0.5,
    runeColor: palette.runeColor,
    runeAlpha: isSelf ? 0.64 : 0.44,
    innerColor: palette.innerColor,
    innerAlpha: isSelf ? 0.3 : 0.2,
  };
}

function blendRgb(base: number, overlay: number, overlayRatio: number): number {
  const ratio = Math.max(0, Math.min(1, overlayRatio));
  const inverse = 1 - ratio;
  const red = Math.round(
    ((base >> 16) & 0xff) * inverse + ((overlay >> 16) & 0xff) * ratio,
  );
  const green = Math.round(
    ((base >> 8) & 0xff) * inverse + ((overlay >> 8) & 0xff) * ratio,
  );
  const blue = Math.round((base & 0xff) * inverse + (overlay & 0xff) * ratio);
  return (red << 16) | (green << 8) | blue;
}
